import React from 'react';
import {Button, Card, Checkbox, Divider, Form, Input, Space, Typography} from 'antd';
import {GoogleOutlined, LinkedinOutlined, LockOutlined, UserOutlined} from '@ant-design/icons';
import {Link, useNavigate} from 'react-router-dom';
import '../../styles/LoginForm.css';
import {useAuth} from "../../contexts/AuthContext";
import { useError } from '../../contexts/ErrorContext';
import { useErrorHandler } from '../../hooks/useErrorHandler';

const {Title, Text} = Typography;

const LoginForm: React.FC = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [linkedInLoading, setLinkedInLoading] = React.useState(false);
    const [googleLoading, setGoogleLoading] = React.useState(false);
    const { showError } = useError();
    const { loading, handleAsync } = useErrorHandler();

    const { login, initiateLinkedInLogin, initiateGoogleLogin } = useAuth();

    const onFinish = async (values: any) => {
        const result = await handleAsync(
            async () => {
                await login(values.email, values.password);
                return true;
            }
        );

        if (result) {
            navigate('/');
        }
    };

    const handleLinkedInLogin = async () => {
        setLinkedInLoading(true);
        try {
            await initiateLinkedInLogin();
        } catch (error) {
            showError('Failed to initiate LinkedIn login');
            setLinkedInLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setGoogleLoading(true);
        try {
            await initiateGoogleLogin();
        } catch (error) {
            showError('Failed to initiate Google login');
            setGoogleLoading(false);
        }
    };

    return (
        <Card
            className="login-form-card"
            variant="borderless"
            style={{
                width: '100%',
                maxWidth: '450px',
                borderRadius: '8px'
            }}
            styles={{ body: { padding: '32px' } }}
        >
            <div style={{textAlign: 'center', marginBottom: '24px'}}>
                <Title level={3}>Welcome Back</Title>
                <Text type="secondary">Sign in to your account</Text>
            </div>

            <Form
                form={form}
                onFinish={onFinish}
                layout="vertical"
                size="large"
            >
                <Form.Item
                    name="email"
                    rules={[
                        {required: true, message: 'Please input your email!'},
                        {type: 'email', message: 'Please enter a valid email!'}
                    ]}
                >
                    <Input
                        prefix={<UserOutlined/>}
                        placeholder="Email address"
                    />
                </Form.Item>

                <Form.Item
                    name="password"
                    rules={[
                        {required: true, message: 'Please input your password!'},
                        {min: 6, message: 'Password must be at least 6 characters!'}
                    ]}
                >
                    <Input.Password
                        prefix={<LockOutlined/>}
                        placeholder="Password"
                    />
                </Form.Item>

                <Space style={{width: '100%', justifyContent: 'space-between'}}>
                    <Form.Item name="remember" valuePropName="checked" noStyle>
                        <Checkbox>Remember me</Checkbox>
                    </Form.Item>
                    <Link to="/forgot-password">
                        <Text type="secondary">Forgot password?</Text>
                    </Link>
                </Space>

                <Form.Item style={{marginTop: '24px'}}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        loading={loading}
                    >
                        Sign In
                    </Button>
                </Form.Item>
            </Form>

            <Divider plain>or continue with</Divider>

            <Space size="large" style={{width: '100%', justifyContent: 'center'}}>
                <Button
                    shape="circle"
                    icon={<LinkedinOutlined/>}
                    onClick={handleLinkedInLogin}
                    loading={linkedInLoading}
                    style={{
                        backgroundColor: '#0077b5',
                        borderColor: '#0077b5',
                        color: '#fff'
                    }}
                />
                <Button
                    shape="circle"
                    icon={<GoogleOutlined/>}
                    onClick={handleGoogleLogin}
                    loading={googleLoading}
                    style={{
                        backgroundColor: '#db4437',
                        borderColor: '#db4437',
                        color: '#fff'
                    }}
                />
            </Space>

            <div style={{textAlign: 'center', marginTop: '24px'}}>
                <Text type="secondary">Don't have an account? </Text>
                <Link to="/register">Sign up</Link>
            </div>
        </Card>
    );
};

export default LoginForm;